import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { SIGN_OUT_CLIENT } from '../../graphql/auth/mutations';
import type { SignOutClientResponse } from '../../generated/graphql';
import { clearTokens } from '../../utils/tokenManager';
import { showToast } from '../../utils/toastHelpers';
import { useTranslation } from 'react-i18next';
import { getOrCreateDeviceId } from '../../utils/deviceUtils';

export const useSignOut = (onSuccess?: () => void) => {
  const { t } = useTranslation();
  /**
   * True from the moment the session is torn down until the replacement
   * document takes over. See the comment on the redirect below: without it the
   * sign-in screen paints twice, which reads as a double reload.
   */
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [signOutMutation, { loading }] = useMutation<{
    signOutClient?: SignOutClientResponse;
  }>(SIGN_OUT_CLIENT);

  /**
   * Sign out the current device or all devices
   * @param logoutAllDevices If true, logs out all devices; if false/undefined, logs out only current device
   */
  const handleSignOut = async (logoutAllDevices?: boolean) => {
    try {
      // Get the current device ID for single-device logout
      const deviceId = logoutAllDevices ? undefined : getOrCreateDeviceId();

      const response = await signOutMutation({
        variables: {
          deviceId,
          logoutAllDevices,
        },
      });

      if (response.data?.signOutClient) {
        // Clear tokens and local storage
        clearTokens();
        showToast(t('loggedOutSuccessfully'), 'success');

        /**
         * A full page load is what guarantees the next visitor gets a clean
         * Apollo cache and no socket of the previous session — but it only
         * commits once the server answers, and until then this document is
         * still alive and still rendering. `onSuccess` drops the user from the
         * context, so `AuthGuard` routes to /signin inside the dying document:
         * the sign-in screen painted, then the arriving document painted it
         * again. That is the double reload. The overlay covers the gap, so a
         * slow response reads as one step instead of two.
         */
        setIsSigningOut(true);

        // Call the optional success callback (for context provider)
        if (onSuccess) {
          onSuccess();
        }

        /**
         * Straight to /signin, and `replace` rather than an assignment: '/'
         * only bounced through `RootRedirect` for the same destination, and
         * leaving /dashboard in the history meant Back landed on a page the
         * session no longer had.
         */
        window.location.replace('/signin');
      }
    } catch (error) {
      console.error('Error during sign out:', error);
      showToast(t('failedToLogout'), 'error');
    }
  };

  return {
    signOut: handleSignOut,
    isLoading: loading,
    isSigningOut,
  };
};
